import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { InputTypes } from 'src/app/constants';
import {
  UserFilterField,
  RuleOperators,
  CriteriaField,
  ActivityFilterField,
  ItemFilterField
} from './fields';
import { MerchantObject } from '../../merchant/merchant.class';

export enum RuleStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  /** Base Rule only — selectable/savable, unlike Bonus Rule's computed "expired" badge. */
  EXPIRED = 'Expired'
}

export enum FilterFormNames {
  ACTIVITY_FILTERS = 'activityFilters',
  USER_FILTERS = 'userFilters',
  ITEM_FILTERS = 'itemFilters',
  ITEM_EXCLUSION_FILTERS = 'itemExclusionFilters',
  CRITERIA = 'criteria'
}

export enum RuleDynamicTypes {
  ACTIVITY_LEVEL_RULE = 'activityLevelRule',
  ITEM_LEVEL_RULE = 'itemLevelRule',
  ITEM_COMBINATIONS_RULE = 'itemCombinationsRule',
  ACTIVITY_COMBINATIONS_RULE = 'activityCombinationsRule',
  SCHEDULED_RULE = 'scheduled-rule',
  ACHIEVEMENT_RULE = 'activity-combination'
}

export enum RuleTypeValues {
  REAL_TIME = 'realTime',
  SCHEDULED = 'scheduled',
  ACHIEVEMENT = 'achievement'
}

export enum AdditionalFieldTypes {
  STRING = 'string',
  BOOLEAN = 'boolean',
  INTEGER = 'integer',
  ENUM = 'enum',
  DATE = 'date',
  ALL_TYPES = 'allTypes'
}

export enum ExpirationUnits {
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year'
}
export interface FilterFormGroupForModel {
  title: string;
  formName: string;
  displayName?: string;
  controls?: IControls[] | undefined;
  placeholder?: string;
  options?: FilterControlForModel[];
}

export interface IControls {
  title: string;
  placeholder?: string;
  formName: string;
  options?: FilterControlForModel[];
  optionsFormName?: string;
}

export interface IOptions {
  value: string;
  viewValue: string;
}

export interface FilterControlForModel {
  value: string;
  viewValue: string;
  operators?: IOptions[];
  inputType?: InputTypes;
  validatorRegex?: RegExp;
  options?: IOptions[];
  patternErrorMessage?: string;
  customValidator?: boolean;
  disabled?: boolean;
  className?: string;
  isAdditionalField?: boolean;
}

export enum AllocationTypes {
  FLAT = 'flat',
  PERCENT = 'percent',
  FORMULA_BASED = 'formula_based',
  DISTRIBUTION_BASED = 'value_distribution',
  USER_BALANCE = 'user_balance'
}

export interface AmountOfPointsForm {
  allocationType: FormControl<AllocationTypes | null>;
  points: FormControl<number | null>;
  percentageOfAmount: FormControl<number | null>;
  roundOffSettings: FormControl<string>;
  factor: FormControl<number | null>;
  operator: FormControl<string>;
  rangeBased: FormControl<boolean>;
  noOfRanges: FormControl<number | null>;
  rangeOfPoints: FormArray<FormGroup<RangeForm>>;
}

export interface RangeForm {
  rangeMin: FormControl<number | null>;
  rangeMax: FormControl<number | null>;
  points: FormControl<number | null>;
}
export interface ValidityForm {
  from: FormControl<string | null>;
  till: FormControl<string | null>;
}

export interface ExpirationForm {
  value: FormControl<number | null>;
  unit: FormControl<ExpirationUnits | null>;
  roundUpSetting: FormControl<string | null>;
}

export interface ValidityExpirationForm {
  value: FormControl<number | null>;
  unit: FormControl<string | null>;
}

interface ValueWithTimePeriodForm {
  limitProperty: FormControl<boolean>;
  value: FormControl<number | null>;
  timePeriod: FormGroup<ValidityExpirationForm>;
}

interface TimePeriodForm {
  timePeriod: FormGroup<ValidityExpirationForm>;
}
export interface ValueWithTimePeriod extends ValueWithTimePeriodForm {
  rewardPeriodType?: FormControl<string | null>;
}

export interface CouponRewardRowForm {
  couponGroupId: FormControl<string | null>;
  quantity: FormControl<number | null>;
}

export interface LimitsForm {
  limitPoints: FormControl<boolean>;
  pointsPerActivity: FormControl<number | null>;
  cappingPoints?: FormControl<boolean>;
  transactionLimit?: FormControl<boolean>;
  pointsPerUser?: FormGroup<ValueWithTimePeriod>;
  pointsCumulative: FormGroup<ValueWithTimePeriodForm>;
  triggersPerUser: FormGroup<ValueWithTimePeriod>;
  // Coupon capping fields
  cappingCoupon?: FormControl<boolean>;
  limitCoupons?: FormControl<boolean>;
  couponsPerActivity?: FormControl<number | null>;
  couponsCumulative?: FormGroup<ValueWithTimePeriodForm>;
  couponsPerUser?: FormGroup<ValueWithTimePeriod>;
}
export interface RuleForm {
  ruleDisplayText: FormControl<string>;
  ruleCode: FormControl<string>;
  ruleDescription: FormControl<string>;
  errorMsg: FormControl<string>;
  ruleMessage?: FormControl<string>;
  ruleTnc?: FormControl<string>;
  status: FormControl<RuleStatus>;
  isPointReward: FormControl<boolean>;
  isCouponReward: FormControl<boolean>;
  couponsGroups?: FormControl<string>;
  searchCouponText?: FormControl<string>;
  couponRewards?: FormArray<FormGroup<CouponRewardRowForm>>;
  validity: FormGroup<ValidityForm>;
  combineActivitiesFrom: FormGroup<TimePeriodForm>;
  combineActivitiesFromPast: FormControl<boolean>;
  searchPartner: FormControl<string>;
  partner: FormControl<MerchantObject[]>;
  rewardType: FormControl<string>;
  isCurrency?: FormControl<boolean>;
  pointsMultiplierPartners?: FormControl<MerchantObject[]>;
  pointsMultiplier: FormGroup<PointsMultiplierForm>;
  isRewardedPerUnit?: FormControl<boolean>;
}
export interface RewardExpirationForm {
  pointsExpiration: FormControl<boolean>;
  timePeriod: FormGroup<ValidityExpirationForm>;
  roundUpSetting: FormControl<string | null>;
}

export interface PointsMultiplierForm {
  startDate: FormControl<string | null>;
  endDate: FormControl<string | null>;
  computeAfterInDays: FormControl<number | null>;
}

export interface RewardsForm {
  amountOfPoints: FormGroup<AmountOfPointsForm>;
  limits: FormGroup<LimitsForm>;
  expiration: FormGroup<ExpirationForm>;
}

export interface BaseFilterForm {
  //TODO: Remove this null
  operator: FormControl<RuleOperators | null>;
  operatorOptions?: FormControl<string>;
  value: FormControl<string | string[]>;
  conditionOptions?: FormControl<string>;
  inputType?: FormControl<string>;
  errorText?: FormControl<string>;
  fileName?: FormControl<string>;
}
export interface UserFilterForm extends BaseFilterForm {
  //TODO: Remove this null
  field: FormControl<UserFilterField | null>;
  additionalFieldName: FormControl<string>;
}

export interface LockPeriodFilterForm extends ActivityFilterForm {
  activityCode: FormControl<string>;
  timePeriod: FormGroup<ValidityExpirationForm>;
  showFilter?: FormControl<boolean>;
}

export interface ActivityFilterForm extends BaseFilterForm {
  field: FormControl<string | null>;
  additionalFieldName?: FormControl<string>;
}

export interface ItemFilterForm extends BaseFilterForm {
  //TODO: Remove this null
  field: FormControl<string | null>;
  additionalFieldName?: FormControl<string>;
}

export interface CriteriaForm {
  //TODO: Remove this null
  criteriaType: FormControl<CriteriaField | null>;
  minimumValue: FormControl<number | null>;
  activityCode?: FormControl<string | null>;
  itemFilters?: FormArray<FormGroup<ItemFilterForm>>;
  activityFilters?: FormArray<FormGroup<ActivityFilterForm>>;
}

export interface BaseFilter<T> {
  additionalFieldName?: string;
  field: T | string | null;
  operator: RuleOperators | null;
  value: string | string[];
  operatorOptions?: string;
  conditionOptions?: string;
  inputType?: string;
  errorText?: string;
  fileName?: string;
}

export interface FilterFormValue {
  activityCode?: string | null;
  activityFilters?: BaseFilter<ActivityFilterField>[];
  userFilters: BaseFilter<UserFilterField>[];
  itemFilters?: BaseFilter<ItemFilterField>[];
  itemExclusionFilters?: BaseFilter<ItemFilterField>[];
  itemExclusionAllowPartialEarning?: boolean;
}

export interface CriteriaFormValue {
  criteria?: Array<{
    criteriaType: CriteriaField | null;
    minimumValue: number | null;
    activityCode?: string | null;
    activityFilters?: Array<{
      additionalFieldName?: string;
      field: string | null;
      operator: RuleOperators | null;
      value: string | string[];
    }>;
    itemFilters?: Array<{
      additionalFieldName?: string;
      field: string | null;
      operator: RuleOperators | null;
      value: string | string[];
    }>;
  }>;
}

export interface CohortFormValue {
  mode: string;
  operator: string;
  searchCohortText?: string;
}

export interface AmountPointsFormValue {
  allocationType: AllocationTypes | null;
  roundOffSettings?: string;
  rangeBased?: boolean;
  rangeOfPoints?: Array<{
    rangeMin: number | null;
    rangeMax: number | null;
    points: number | null;
  }>;
  points?: number;
  percentageOfAmount?: number;
  factor?: number;
}

export interface RuleFormRawValue {
  ruleCode: string;
  isRewardedPerUnit?: boolean;
  ruleDisplayText: string;
  isCouponReward?: boolean;
  couponsGroups?: string;
  couponRewards?: Array<{
    couponGroupId: string | null;
    quantity: number | null;
  }>;
  isCurrency?: boolean;
  partner: MerchantObject[];
  ruleDescription?: string;
  ruleMessage?: string;
  ruleTnc?: string;
  isPointReward?: boolean;
  rewardType?: string;
  pointsMultiplierPartners?: Array<{
    _id: string | undefined;
    name: string;
  }>;
  pointsMultiplier?: {
    [key: string]: unknown;
  };
  combineActivitiesFrom?: {
    timePeriod: {
      value: number;
      unit: string;
    };
  };
  validity: {
    from: string | null;
    till: string | null;
  };
  errorMsg?: string;
}

export interface LimitsRawValue {
  limitPoints: boolean;
  pointsPerActivity: number | null;
  cappingPoints?: boolean;
  transactionLimit?: boolean;
  pointsCumulative: {
    limitProperty: boolean;
    value: number | null;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
  triggersPerUser: {
    limitProperty: boolean;
    value: number | null;
    rewardPeriodType?: string | undefined | null;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
  pointsPerUser: {
    limitProperty: boolean;
    value: number | null;
    rewardPeriodType?: string | undefined;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
  // Coupon capping raw values
  cappingCoupon?: boolean;
  limitCoupons?: boolean;
  couponsPerActivity?: number | null;
  couponsCumulative?: {
    limitProperty: boolean;
    value: number | null;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
  couponsPerUser?: {
    limitProperty: boolean;
    value: number | null;
    rewardPeriodType?: string | undefined;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
}

export interface LimitsData {
  pointsPerActivity?: number;
  pointsCumulative?: {
    value: number | null;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
  triggersPerUser?:
    | number
    | {
        value: number | null;
        timePeriod: {
          value: number | null;
          unit: string | null;
        };
      };
  rollingTriggersPerUser?: {
    value: number | null;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
  pointsPerUser?:
    | number
    | {
        value: number | null;
        timePeriod: {
          value: number | null;
          unit: string | null;
        };
      };
  rollingPointsPerUser?: {
    value: number | null;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
  // Coupon capping data
  couponsPerActivity?: number;
  couponsCumulative?: {
    value: number | null;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
  couponsPerUser?:
    | number
    | {
        value: number | null;
        timePeriod: {
          value: number | null;
          unit: string | null;
        };
      };
  rollingCouponsPerUser?: {
    value: number | null;
    timePeriod: {
      value: number | null;
      unit: string | null;
    };
  };
}
